'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import enTranslations from '../public/translations/en.json';



type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRTL: boolean;
  isAdmin: boolean;
  setIsAdmin: (isAdmin: boolean) => void;
  isFrozen: boolean;
  setIsFrozen: (isFrozen: boolean) => void;
}



const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');
  const [translations, setTranslations] = useState<any>(enTranslations);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isRTL = language === 'ar';
  const [mounted, setMounted] = useState(false);

  // Only run on client-side after component is mounted
  useEffect(() => {
    setMounted(true);

    // Get initial language from localStorage or browser
    try {
      const savedLanguage = localStorage.getItem('language') as Language;
      const browserLanguage = navigator.language.startsWith('ar') ? 'ar' : 'en';
      setLanguage(savedLanguage || browserLanguage);

      // Check for admin session
      const adminSession = localStorage.getItem('isAdmin') === 'true';
      setIsAdmin(adminSession);
    } catch (error) {
      // Fallback if localStorage is not available
      console.error('Error accessing browser APIs:', error);
    }
    // Mark as hydrated first to prevent hydration mismatch
    setIsHydrated(true);
  }, []);

  // Update isAdmin in localStorage when it changes
  useEffect(() => {
    if (!isHydrated) return;
    try {
      localStorage.setItem('isAdmin', isAdmin.toString());
    } catch (e) {
      // storage unavailable (e.g. iOS "Block All Cookies") - safe to ignore
    }
  }, [isAdmin, isHydrated]);

  // Only run DOM manipulation on client-side after component is mounted
  useEffect(() => {
    if (!mounted) return;

    // Only manipulate DOM after hydration to prevent mismatch
    if (!isHydrated) return;

    // Update document direction based on language
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;

    // Add RTL class to body for global styling
    if (isRTL) {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }

    // Add Frozen class to body for global animation freezing
    if (isFrozen) {
      document.body.classList.add('frozen');
    } else {
      document.body.classList.remove('frozen');
    }
  }, [language, isRTL, mounted, isHydrated, isFrozen]);

  // Fetch translations when language changes
  useEffect(() => {
    const fetchTranslations = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`/translations/${language}.json`);
        const data = await response.json();
        setTranslations(data);
      } catch (error) {
        console.error('Failed to load translations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isHydrated) {
      if (language === 'en') {
        setTranslations(enTranslations);
        setIsLoading(false);
      } else {
        fetchTranslations();
      }
    }
  }, [language, isHydrated]);

  // Save language preference after hydration
  useEffect(() => {
    if (!isHydrated) return;
    try {
      localStorage.setItem('language', language);
    } catch (e) {
      // ignore if storage not available
    }
  }, [language, isHydrated]);


  const t = (key: string): string => {
    return translations[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isRTL, isAdmin, setIsAdmin, isFrozen, setIsFrozen }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
